/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React from 'react';
import { Debate } from '../types';
import { Typography, Box, Divider, Paper, Skeleton, Alert } from '@mui/material';
import '../styles/DebateSummary.css';
import { getTeamName } from '../utils/teamUtils';

interface DebateSummaryProps {
  debate: Debate;
}

const DebateSummary: React.FC<DebateSummaryProps> = ({ debate }) => {
  // Check for missing debate object
  if (!debate) {
    return (
      <Box className="debate-summary">
        <Typography variant="h5" gutterBottom>Debate Summary</Typography>
        <Alert severity="error">Cannot display summary - debate data is missing.</Alert>
      </Box>
    );
  }

  // Handle case where summary doesn't exist yet
  if (!debate.summary) {
    return (
      <Box className="debate-summary">
        <Typography variant="h5" gutterBottom>Debate Summary</Typography>
        <Alert severity="info">
          The summary for this debate is being generated. Please check back shortly.
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="text" height={60} />
          <Skeleton variant="text" height={60} />
          <Skeleton variant="text" height={60} />
        </Box>
      </Box>
    );
  }

  // Handle empty summary
  if (debate.summary.trim() === '') {
    return (
      <Box className="debate-summary">
        <Typography variant="h5" gutterBottom>Debate Summary</Typography>
        <Alert severity="warning">No summary is available for this debate.</Alert>
      </Box>
    );
  }

  return (
    <Box className="debate-summary">
      <Typography variant="h5" gutterBottom>Debate Summary</Typography>
      <Box className="summary-content" sx={{ my: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        {debate.summary.split('\n').map((paragraph: string, index: number) => (
          <Typography key={index} variant="body1" paragraph>
            {paragraph}
          </Typography>
        ))}
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box className="debate-info">
        <Box className="debate-info-item">
          <Typography variant="subtitle2" color="text.secondary">Topic:</Typography>
          <Typography variant="body1" fontWeight="bold">{debate.topic}</Typography>
        </Box>
        <Box className="debate-info-item" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Teams:</Typography>
          <Box className="teams-list">
            {debate.teams && debate.teams.map((team: any) => (
              <Paper key={team._id || team.id} className="team-item" elevation={1} sx={{ p: 1, mt: 1 }}>
                <Typography variant="subtitle1" className="team-name">{team.name}</Typography>
                <Typography variant="body2" className="team-viewpoint" color="text.secondary">
                  {team.viewpoint}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
        {debate.winner && (
          <Box className="debate-info-item" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Winner:</Typography>
            <Typography variant="body1" color="success.main" fontWeight="bold">
              {getTeamName(debate.winner, debate)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DebateSummary; 